import React, { useEffect, useMemo, useState } from 'react';
import { Card, Col, Descriptions, Divider, Row, Space, Typography, Button, Tag, Spin } from 'antd';
import { LinkOutlined, SafetyOutlined, SettingOutlined } from '@ant-design/icons';
import { useKeycloak } from '@react-keycloak/web';
import MainLayout from '../layouts/MainLayout';
import { getDestineIamLinkStatus } from '../services/accountService';
import UserHistoryPanel from '../components/history/UserHistoryPanel';

const { Text } = Typography;

function formatDateTime(seconds) {
  if (!seconds) return '—';
  return new Date(seconds * 1000).toLocaleString();
}

function formatDuration(ms) {
  if (ms == null || Number.isNaN(ms)) return '—';
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function getClientInfo() {
  const ua = navigator.userAgent || '';
  const platform = navigator.platform || '';

  const isWindows = /Windows/i.test(ua);
  const isMac = /Macintosh|Mac OS X/i.test(ua);
  const isLinux = /Linux/i.test(ua) && !/Android/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);

  const os =
    (isWindows && 'Windows') ||
    (isMac && 'macOS') ||
    (isIOS && 'iOS') ||
    (isAndroid && 'Android') ||
    (isLinux && 'Linux') ||
    platform ||
    'Unknown';

  const isEdge = /Edg\//.test(ua);
  const isChrome = /Chrome\//.test(ua) && !isEdge;
  const isSafari = /Safari\//.test(ua) && !isChrome && !isEdge;
  const isFirefox = /Firefox\//.test(ua);

  const browser =
    (isEdge && 'Edge') ||
    (isChrome && 'Chrome') ||
    (isFirefox && 'Firefox') ||
    (isSafari && 'Safari') ||
    'Unknown';

  return { os, browser, userAgent: ua };
}

function AccountView() {
  const { keycloak } = useKeycloak();
  const [destineIam, setDestineIam] = useState({
    loading: true,
    linked: false,
    linkedUsername: null,
    signedInViaDestine: false,
    statusKnown: false,
  });

  const token = keycloak?.tokenParsed || {};
  const { os, browser, userAgent } = useMemo(() => getClientInfo(), []);

  const nowMs = Date.now();
  const expMs = token.exp ? token.exp * 1000 : null;
  const expiresInMs = expMs ? expMs - nowMs : null;

  useEffect(() => {
    if (!keycloak?.authenticated) {
      setDestineIam((prev) => ({ ...prev, loading: false }));
      return;
    }

    let cancelled = false;
    setDestineIam((prev) => ({ ...prev, loading: true }));

    getDestineIamLinkStatus(keycloak)
      .then((status) => {
        if (cancelled) return;
        setDestineIam({
          loading: false,
          linked: status.linked,
          linkedUsername: status.linkedUsername,
          signedInViaDestine: status.signedInViaDestine,
          statusKnown: status.statusKnown,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setDestineIam((prev) => ({ ...prev, loading: false, statusKnown: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [keycloak, keycloak?.token]);

  const openAccountConsole = (pathHash) => {
    const base = keycloak.createAccountUrl();
    const url = pathHash ? `${base}${pathHash}` : base;
    window.open(url, '_self');
  };

  const destineStatusTag = () => {
    if (destineIam.loading) {
      return <Spin size='small' />;
    }
    if (destineIam.linked) {
      return <Tag color='success'>Linked</Tag>;
    }
    return <Tag>Not linked</Tag>;
  };

  const destineActionLabel = destineIam.linked ? 'Manage DestinE IAM' : 'Link DestinE IAM';

  return (
    <MainLayout menu='account'>
      <div className='max-w-7xl mx-auto p-6'>
        <Space direction='vertical' size='large' style={{ width: '100%' }}>
          <Text type='secondary'>Manage your profile, security, and session details.</Text>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <Card title='Profile'>
                <Descriptions size='small' column={1} bordered={false}>
                  <Descriptions.Item label='Username'>{token.preferred_username || '—'}</Descriptions.Item>
                  <Descriptions.Item label='Name'>{token.name || '—'}</Descriptions.Item>
                  <Descriptions.Item label='Email'>
                    {token.email ? (
                      <>
                        {token.email}{' '}
                        {token.email_verified ? <Tag color='success'>Verified</Tag> : <Tag>Unverified</Tag>}
                      </>
                    ) : (
                      '—'
                    )}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card
                title='Session'
                extra={expMs ? <Tag color={expiresInMs > 5 * 60 * 1000 ? 'success' : 'warning'}>Active</Tag> : null}
              >
                <Descriptions size='small' column={1} bordered={false}>
                  <Descriptions.Item label='Last login'>
                    {formatDateTime(token.auth_time) || formatDateTime(token.iat)}
                  </Descriptions.Item>
                  <Descriptions.Item label='Session expires'>{formatDateTime(token.exp)}</Descriptions.Item>
                  <Descriptions.Item label='Time remaining'>{formatDuration(expiresInMs)}</Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title='Client device'>
                <Descriptions size='small' column={1} bordered={false}>
                  <Descriptions.Item label='OS'>{os}</Descriptions.Item>
                  <Descriptions.Item label='Browser'>{browser}</Descriptions.Item>
                  <Descriptions.Item label='User agent'>
                    <Text type='secondary' style={{ wordBreak: 'break-word' }}>
                      {userAgent || '—'}
                    </Text>
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>

            <Col xs={24} lg={12}>
              <Card title='Security & integrations'>
                <Space direction='vertical' size='middle' style={{ width: '100%' }}>
                  <div>
                    <Text strong>Password</Text>
                    <div style={{ marginTop: 8 }}>
                      <Button icon={<SafetyOutlined />} onClick={() => openAccountConsole('#/security/signingin')}>
                        Change password
                      </Button>
                    </div>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  <div>
                    <Text strong>DestinE IAM</Text>
                    <Descriptions size='small' column={1} bordered={false} style={{ marginTop: 8 }}>
                      <Descriptions.Item label='Status'>{destineStatusTag()}</Descriptions.Item>
                      {destineIam.linkedUsername ? (
                        <Descriptions.Item label='Linked account'>{destineIam.linkedUsername}</Descriptions.Item>
                      ) : null}
                    </Descriptions>
                    {destineIam.signedInViaDestine && !destineIam.linked && !destineIam.loading ? (
                      <Text type='secondary' style={{ display: 'block', marginTop: 4 }}>
                        You are signed in with DestinE IAM for this session.
                      </Text>
                    ) : null}
                    {!destineIam.statusKnown && !destineIam.loading ? (
                      <Text type='secondary' style={{ display: 'block', marginTop: 4 }}>
                        Link status could not be verified. Use manage to link or unlink in account settings.
                      </Text>
                    ) : null}
                    <div style={{ marginTop: 8 }}>
                      <Button icon={<SettingOutlined />} onClick={() => openAccountConsole('#/identity')}>
                        {destineActionLabel}
                      </Button>
                    </div>
                  </div>

                  <Divider style={{ margin: '8px 0' }} />

                  <div>
                    <Text strong>Advanced settings</Text>
                    <div style={{ marginTop: 8 }}>
                      <Button icon={<LinkOutlined />} onClick={() => openAccountConsole()}>
                        Open account settings
                      </Button>
                    </div>
                    <Text type='secondary' style={{ display: 'block', marginTop: 8 }}>
                      Full account console for password, identity providers, and other security options.
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>

          <div style={{ marginTop: 16 }}>
            <UserHistoryPanel title='History' compact />
          </div>
        </Space>
      </div>
    </MainLayout>
  );
}

export default AccountView;
